import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  BookOpenCheck,
  Boxes,
  Calculator,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  CreditCard,
  DatabaseZap,
  ExternalLink,
  Factory,
  Landmark,
  LayoutDashboard,
  Library,
  LifeBuoy,
  Menu,
  Printer,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Store,
  TicketPercent,
  Users,
  Workflow,
  X,
  Zap,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import {
  documentationMeta,
  documentationSections,
  flattenDocumentationText,
} from '../data/erpDocumentation';

const ICONS = {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  BookOpenCheck,
  Boxes,
  Calculator,
  ClipboardList,
  CreditCard,
  DatabaseZap,
  Factory,
  Landmark,
  LayoutDashboard,
  Library,
  LifeBuoy,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  TicketPercent,
  Users,
  Workflow,
  Zap,
};

const LANGUAGE_OPTIONS = [
  { value: 'both', label: 'Both' },
  { value: 'en', label: 'English' },
  { value: 'si', label: 'සිංහල' },
];

const TONE_STYLES = {
  info: 'border-blue-200 bg-blue-50 text-blue-950',
  warning: 'border-amber-200 bg-amber-50 text-amber-950',
  danger: 'border-red-200 bg-red-50 text-red-950',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-950',
};

const LanguageText = ({ value, mode, className = '' }) => {
  if (!value) return null;
  return (
    <div className={`space-y-2 ${className}`}>
      {(mode === 'both' || mode === 'en') && value.en && (
        <p className="leading-7 text-slate-700" lang="en">{value.en}</p>
      )}
      {(mode === 'both' || mode === 'si') && value.si && (
        <p
          className={mode === 'both'
            ? 'rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 leading-7 text-slate-700'
            : 'leading-7 text-slate-700'}
          lang="si"
        >
          {mode === 'both' && <span className="mr-2 text-xs font-bold uppercase tracking-wide text-indigo-700">සිංහල</span>}
          {value.si}
        </p>
      )}
    </div>
  );
};

const BilingualList = ({ items, mode, ordered = false, checklist = false }) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`${ordered ? 'list-decimal' : 'list-none'} space-y-3 ${ordered ? 'pl-6' : ''}`}>
      {items.map((item, index) => (
        <li key={`${item.en}-${index}`} className={ordered ? 'pl-1' : ''}>
          <div className="flex items-start gap-3">
            {!ordered && (
              <span className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${checklist ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                {checklist ? <Check className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </span>
            )}
            <LanguageText value={item} mode={mode} className="min-w-0 flex-1" />
          </div>
        </li>
      ))}
    </Tag>
  );
};

const DocumentationBlock = ({ block, languageMode }) => {
  if (block.type === 'callout') {
    return (
      <div className={`rounded-xl border p-4 ${TONE_STYLES[block.tone] || TONE_STYLES.info}`}>
        <div className="mb-2 flex items-center gap-2">
          {block.tone === 'danger' || block.tone === 'warning'
            ? <ShieldAlert className="h-5 w-5 shrink-0" />
            : <BookOpenCheck className="h-5 w-5 shrink-0" />}
          <h3 className="font-bold">{block.title}</h3>
        </div>
        <LanguageText value={block.text} mode={languageMode} />
      </div>
    );
  }

  if (block.type === 'journal') {
    return (
      <div>
        <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
        <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-sm">
          <div className="border-b border-slate-800 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">
            Double Entry
          </div>
          <div className="space-y-1 px-4 py-4 font-mono text-sm text-slate-100">
            {block.lines.map((line, index) => line
              ? <div key={`${line}-${index}`}>{line}</div>
              : <div key={`space-${index}`} className="h-2" />)}
          </div>
        </div>
        <LanguageText value={block.text} mode={languageMode} className="mt-3" />
      </div>
    );
  }

  if (block.type === 'table') {
    return (
      <div>
        <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                {block.columns.map((column) => (
                  <th key={column} className="border-b px-4 py-3 font-bold text-slate-800">{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`} className="align-top even:bg-slate-50/60">
                  {row.map((item, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`} className="border-b px-4 py-3 last:border-r-0">
                      <LanguageText value={item} mode={languageMode} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (block.type === 'workflow') {
    return (
      <div>
        <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
        <div className="grid gap-2">
          {block.items.map((item, index) => (
            <React.Fragment key={`${item.en}-${index}`}>
              <div className="flex items-stretch gap-3">
                <div className="flex w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 font-bold text-white">
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1 rounded-lg border bg-slate-50 px-4 py-3">
                  <LanguageText value={item} mode={languageMode} />
                </div>
              </div>
              {index < block.items.length - 1 && (
                <div className="ml-4 h-4 border-l-2 border-dashed border-indigo-200" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === 'steps') {
    return (
      <div>
        <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
        <BilingualList items={block.items} mode={languageMode} ordered />
      </div>
    );
  }

  if (block.type === 'checklist') {
    return (
      <div>
        <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
        <BilingualList items={block.items} mode={languageMode} checklist />
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 text-lg font-bold text-slate-900">{block.title}</h3>
      <BilingualList items={block.items || []} mode={languageMode} />
    </div>
  );
};

export default function Documentation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(documentationSections[0].id);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [languageMode, setLanguageMode] = useState(() => localStorage.getItem('erp_docs_language') || 'both');
  const observerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('erp_docs_language', languageMode);
  }, [languageMode]);

  const filteredSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return documentationSections;
    return documentationSections.filter((section) => flattenDocumentationText(section).includes(query));
  }, [searchQuery]);

  useEffect(() => {
    observerRef.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-110px 0px -65% 0px', threshold: [0, 0.05, 0.2] },
    );
    filteredSections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [filteredSections]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
    setMobileTocOpen(false);
  };

  const printGuide = () => window.print();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="documentation-page">
      <style>{`
        @media print {
          .docs-no-print { display: none !important; }
          .docs-print-full { width: 100% !important; max-width: none !important; }
          .docs-section { break-inside: avoid; box-shadow: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <header className="docs-no-print sticky top-0 z-30 border-b bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-[1500px] px-4 py-4 lg:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-emerald-400">
                <BookOpen className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-950">ERP Documentation</h1>
                  <Badge variant="outline">{documentationMeta.version}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Code-audited</Badge>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  English and Sinhala operating guide · Headings remain in English · Audited {documentationMeta.auditedOn}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative min-w-0 sm:w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search English or Sinhala..."
                  className="pl-9"
                  data-testid="doc-search"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex rounded-lg border bg-slate-50 p-1">
                {LANGUAGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setLanguageMode(option.value)}
                    className={`rounded-md px-3 py-2 text-xs font-bold transition ${languageMode === option.value ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <Button type="button" variant="outline" onClick={printGuide}>
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] px-4 py-6 lg:px-6">
        <Card className="mb-6 border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-emerald-50">
          <CardContent className="p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-700">Documentation Status</p>
                <h2 className="mt-1 text-lg font-bold text-slate-950">Aligned with the current ERP codebase</h2>
                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                  Reviewed against {documentationMeta.auditedAgainst}. Outdated primary-WooCommerce instructions, incorrect dashboard formulas, unsafe over-receipt guidance and misleading payment/refund statements were removed or corrected.
                </p>
                <p className="mt-2 max-w-4xl rounded-lg bg-white/80 px-3 py-2 text-sm leading-6 text-slate-700" lang="si">
                  වත්මන් ERP codebase එකට අනුව මෙම documentation update කර ඇත. outdated WooCommerce primary-flow උපදෙස්, වැරදි dashboard formulas, unsafe over-receipt guidance සහ misleading payment/refund details නිවැරදි කර ඇත.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{filteredSections.length} sections</Badge>
                <Badge variant="outline">Role: {user?.role || 'user'}</Badge>
                <Badge variant="outline">Language: {LANGUAGE_OPTIONS.find((option) => option.value === languageMode)?.label}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="docs-no-print mb-4 lg:hidden">
          <Button type="button" variant="outline" className="w-full justify-between" onClick={() => setMobileTocOpen((value) => !value)}>
            <span className="flex items-center gap-2"><Menu className="h-4 w-4" /> Table of Contents</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`} />
          </Button>
          {mobileTocOpen && (
            <Card className="mt-2">
              <CardContent className="max-h-[60vh] overflow-y-auto p-3">
                {filteredSections.map((section) => {
                  const Icon = ICONS[section.icon] || BookOpen;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${activeSection === section.id ? 'bg-indigo-50 font-bold text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{section.title}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex items-start gap-6">
          <aside className="docs-no-print sticky top-28 hidden h-[calc(100vh-8rem)] w-72 shrink-0 overflow-y-auto lg:block">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Table of Contents</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <nav className="space-y-1">
                  {filteredSections.map((section) => {
                    const Icon = ICONS[section.icon] || BookOpen;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition ${activeSection === section.id ? 'bg-indigo-50 font-bold text-indigo-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                        data-testid={`toc-${section.id}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">{section.title}</span>
                        {activeSection === section.id && <ChevronRight className="h-4 w-4" />}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </aside>

          <main className="docs-print-full min-w-0 flex-1">
            {filteredSections.length === 0 ? (
              <Card>
                <CardContent className="p-10 text-center">
                  <Search className="mx-auto h-12 w-12 text-slate-300" />
                  <h2 className="mt-4 text-xl font-bold text-slate-900">No documentation found</h2>
                  <p className="mt-2 text-slate-500">Try a module name, accounting term or Sinhala keyword.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {filteredSections.map((section) => {
                  const Icon = ICONS[section.icon] || BookOpen;
                  return (
                    <Card key={section.id} id={section.id} className="docs-section scroll-mt-28 overflow-hidden">
                      <div className="border-b bg-gradient-to-r from-slate-50 to-white px-5 py-5 sm:px-7">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-emerald-400">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <h2 className="text-2xl font-bold text-slate-950">{section.title}</h2>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Badge variant="outline">{section.audience}</Badge>
                              </div>
                            </div>
                          </div>
                          {section.route && section.route !== '/documentation' && (
                            <Button type="button" variant="outline" size="sm" className="docs-no-print" onClick={() => navigate(section.route)}>
                              Open Module <ExternalLink className="ml-2 h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <LanguageText value={section.intro} mode={languageMode} className="mt-4" />
                      </div>

                      <CardContent className="space-y-7 p-5 sm:p-7">
                        {section.blocks.map((block, index) => (
                          <DocumentationBlock key={`${section.id}-${block.title}-${index}`} block={block} languageMode={languageMode} />
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      {showBackToTop && (
        <Button
          type="button"
          size="icon"
          className="docs-no-print fixed bottom-6 right-6 z-30 rounded-full shadow-xl"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}
